/**
 * Path Union Utility
 * 
 * Computes the boolean union of an SVG shape path with the bounding rectangles
 * of elements that overflow beyond the shape boundary. Uses polygon-clipping
 * for the boolean operation.
 * 
 * Only applies when at least one element extends beyond the shape edge.
 */
import polygonClipping from 'polygon-clipping';

/**
 * Sample an SVG path into polygon points using the browser's SVGPathElement API.
 * @param {string} pathD - SVG path d attribute
 * @param {number} [numSamples=200] - Number of points to sample along the path
 * @returns {Array<[number, number]>} Array of [x, y] points
 */
export function svgPathToPolygon(pathD, numSamples = 200) {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  const path = document.createElementNS(ns, 'path');
  path.setAttribute('d', pathD);
  svg.appendChild(path);
  document.body.appendChild(svg);

  const totalLength = path.getTotalLength();
  const points = [];
  for (let i = 0; i < numSamples; i++) {
    const pt = path.getPointAtLength((i / numSamples) * totalLength);
    points.push([pt.x, pt.y]);
  }

  document.body.removeChild(svg);
  return points;
}

/**
 * Get the axis-aligned bounding box of a polygon.
 * @param {Array<[number, number]>} polygon
 * @returns {{ minX: number, minY: number, maxX: number, maxY: number }}
 */
function polygonBBox(polygon) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of polygon) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
}

/**
 * Create a rectangle polygon (counter-clockwise) with an offset.
 * @param {number} x - Center X
 * @param {number} y - Center Y
 * @param {number} w - Width
 * @param {number} h - Height
 * @param {number} offset - Padding around the rectangle
 * @param {number} [rotation=0] - Rotation in degrees
 * @returns {Array<[number, number]>} Rectangle as polygon points
 */
function makeOffsetRect(x, y, w, h, offset, rotation = 0) {
  const hw = w / 2 + offset;
  const hh = h / 2 + offset;
  // Corners relative to center
  let corners = [
    [-hw, -hh],
    [hw, -hh],
    [hw, hh],
    [-hw, hh],
  ];

  // Apply rotation
  if (rotation !== 0) {
    const rad = (rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    corners = corners.map(([cx, cy]) => [
      cx * cos - cy * sin,
      cx * sin + cy * cos,
    ]);
  }

  // Translate to position
  return corners.map(([cx, cy]) => [x + cx, y + cy]);
}

/**
 * Check if an element's bounding box extends beyond the shape's bounding box.
 * @param {Object} el - Element with x, y, width, height, rotation
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }} shapeBBox
 * @returns {boolean}
 */
function elementOverflows(el, shapeBBox) {
  const rect = makeOffsetRect(el.x, el.y, el.width, el.height, 0, el.rotation || 0);
  const elBBox = polygonBBox(rect);
  return (
    elBBox.minX < shapeBBox.minX ||
    elBBox.minY < shapeBBox.minY ||
    elBBox.maxX > shapeBBox.maxX ||
    elBBox.maxY > shapeBBox.maxY
  );
}

/**
 * Convert polygon-clipping result rings to an SVG path string.
 * @param {Array} multiPolygon - polygon-clipping result
 * @returns {string} SVG path d attribute
 */
function multiPolygonToPath(multiPolygon) {
  const parts = [];
  for (const polygon of multiPolygon) {
    for (const ring of polygon) {
      if (ring.length === 0) continue;
      parts.push(`M ${ring[0][0].toFixed(2)},${ring[0][1].toFixed(2)}`);
      for (let i = 1; i < ring.length; i++) {
        parts.push(`L ${ring[i][0].toFixed(2)},${ring[i][1].toFixed(2)}`);
      }
      parts.push('Z');
    }
  }
  return parts.join(' ');
}

/**
 * Compute the union cutline for a shape + overflowing elements.
 * Returns the original shape path if nothing overflows.
 * 
 * @param {string} shapePathD - The SVG path d attribute of the shape
 * @param {Array<Object>} elements - Array of element objects with { x, y, width, height, rotation }
 * @param {number} offsetPx - Offset padding around overflowing elements (in viewBox units)
 * @param {number} scaleX - Shape stretch X
 * @param {number} scaleY - Shape stretch Y
 * @returns {string} SVG path d for the cutline
 */
export function computeUnionCutline(shapePathD, elements, offsetPx = 10, scaleX = 1, scaleY = 1) {
  if (!shapePathD || !elements || elements.length === 0) return shapePathD;

  // 1. Convert shape path to polygon (at native scale, we'll scale after)
  let shapePolygon = svgPathToPolygon(shapePathD, 300);

  // Apply stretch scaling to the shape polygon
  if (scaleX !== 1 || scaleY !== 1) {
    shapePolygon = shapePolygon.map(([x, y]) => [x * scaleX, y * scaleY]);
  }

  const shapeBBox = polygonBBox(shapePolygon);

  // 2. Find overflowing elements
  const overflowing = elements.filter(el => {
    if (!el.width || !el.height) return false;
    return elementOverflows(el, shapeBBox);
  });

  // If nothing overflows, return original path (with scaling)
  if (overflowing.length === 0) return null; // null = use original

  // 3. Build polygons for union
  // polygon-clipping expects [[[x,y], [x,y], ...]] format (multi-polygon > polygon > ring)
  const shapeForClipping = [[shapePolygon.map(p => [p[0], p[1]])]];

  // Union the shape with each overflowing element's offset rectangle
  let result = shapeForClipping;
  for (const el of overflowing) {
    const rect = makeOffsetRect(el.x, el.y, el.width, el.height, offsetPx, el.rotation || 0);
    const rectForClipping = [[rect.map(p => [p[0], p[1]])]];
    try {
      result = polygonClipping.union(result, rectForClipping);
    } catch (e) {
      console.warn('Union failed for element, skipping:', e);
    }
  }

  // 4. Convert result to SVG path
  return multiPolygonToPath(result);
}
